import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { MeQuery } from '../../../../graphql/queries/account';
import Spinner from '../../../../components/global/Spinner';
import { STRIPE_CHECKOUT_MODAL } from '../../../../components/global/StripeCheckout';
import { formatPriceForDisplay } from '../../../../util/format-money';
import useOpenModal from '../../../../hooks/useOpenModal';
import useEventAddonPurchase from './hooks/useEventAddonPurchase';
import { FREEMIUM_EVENTS_META, getRawEventAddonItemsPrice } from './helpers';
import EventAddonItem from './components/EventAddonItem';
import Button from '../../../../components/global/Button';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../constants/event-items';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  className: PropTypes.string,
  albumFolderId: PropTypes.string,
  albumSubtype: PropTypes.string,
  displayDefaultPackage: PropTypes.bool,
  onConfirmSelection: PropTypes.func,
  forUpgrade: PropTypes.bool,
  defaultSelectedEventAddonItemIds: PropTypes.array,
  initialIncludePhotosAlbum: PropTypes.bool,
  submitBtnClasses: PropTypes.string,
};

const FreemiumEventPurchase = ({
  className,
  user,
  albumFolderId,
  albumSubtype,
  displayDefaultPackage = true,
  onConfirmSelection,
  forUpgrade,
  defaultSelectedEventAddonItemIds = [],
  initialIncludePhotosAlbum = false,
  submitBtnClasses,
}) => {
  const openModal = useOpenModal();
  const [includePhotosAlbum, setIncludePhotosAlbum] = useState(
    initialIncludePhotosAlbum,
  );
  const [basicSelected, setBasicSelected] = useState(false);
  const {
    loadingEventAddonItems,
    eventAddonItems,
    purchasedAddonItems,
    onCheckout,
    submitting,
    selectedEventAddonItemIds,
    onEventAddonItemToggle,
  } = useEventAddonPurchase({
    albumFolderId,
    useEntityAvailableAddonItems: forUpgrade,
    defaultSelectedEventAddonItemIds,
    defaultEventAddonItemTypes: [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    ],
  });

  const waldofiedItem = eventAddonItems.find(
    ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );
  const albumAddonItems = eventAddonItems.filter(
    ({ types }) => types[0] !== EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );

  const meta = FREEMIUM_EVENTS_META[albumSubtype];

  if (loadingEventAddonItems || !eventAddonItems) {
    return (
      <div className="p-5 d-flex">
        <Spinner className="m-auto" />
      </div>
    );
  }

  const classes = classNames({
    'freemium-event-purchase': true,
    [className]: !!className,
  });

  const optionContainerClasses = classNames({
    'freemium-event-options single-option': true,
  });

  const checkoutContainerClasses = classNames({
    'd-flex mt-4': true,
    'justify-content-center': !forUpgrade,
  });

  const selectedEventAddonItems = selectedEventAddonItemIds.map(
    eventAddonItemId =>
      eventAddonItems.find(
        eventAddonItem => eventAddonItem.id === eventAddonItemId,
      ),
  );

  const checkoutPrice = getRawEventAddonItemsPrice({
    eventAddonItems: selectedEventAddonItems,
  });

  const onPurchase = () => {
    if (onConfirmSelection) {
      onConfirmSelection({
        eventAddonItems,
        selectedEventAddonItemIds,
        onCheckout,
        includePhotosAlbum,
      });

      return;
    }

    if (!albumFolderId) {
      // We cannot perform a checkout without albumFolderId
      return;
    }

    openModal(STRIPE_CHECKOUT_MODAL, {
      buttonLabel: 'Complete payment',
      // If the event is FREE, will not require CC
      cardRequired: false,
      onSuccess: token =>
        onCheckout({
          token,
          albumFolderId,
        }),
      accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_addon_items: selectedEventAddonItemIds.map(id => ({
            event_addon_item_id: id,
            quantity: 1,
            album_folder_id: albumFolderId,
          })),
          promo_code: promoCode,
        };
      },
      defaultName: user?.name ?? '',
      defaultEmail: user?.email_address ?? '',
    });
  };

  const waldofiedMeta = meta.addonItemDetails[waldofiedItem?.types?.[0]];

  return (
    <div className={classes}>
      {submitting && (
        <div className="d-flex flex-column justify-content-center align-items-center my-2">
          <span className="text-primary mb-2">
            Processing your request&hellip;
          </span>
          <Spinner size="sm" className="m-auto " />
        </div>
      )}

      <div className={optionContainerClasses}>
        {waldofiedItem && (
          <Fragment>
            {forUpgrade && (
              <span className="text-muted text-uppercase text-extra-bold">
                Upgrades
              </span>
            )}
            <div className="freemium-event-option">
              <EventAddonItem
                key={waldofiedItem.id}
                isSelected={includePhotosAlbum}
                onToggleSelect={() => {
                  // This will be toggling, hence we must remove the waldofied item
                  if (
                    (!includePhotosAlbum && !basicSelected) ||
                    (includePhotosAlbum &&
                      selectedEventAddonItemIds.includes(waldofiedItem.id))
                  ) {
                    onEventAddonItemToggle(waldofiedItem.id);
                  }
                  // Toggle Include Photos Album
                  setIncludePhotosAlbum(prev => !prev);
                }}
                onTogglePlan={() => {
                  if (!displayDefaultPackage) {
                    return;
                  }

                  if (!includePhotosAlbum) {
                    setBasicSelected(prev => !prev);
                    return;
                  }

                  if (selectedEventAddonItemIds.includes(waldofiedItem.id)) {
                    setBasicSelected(true);
                  } else {
                    setBasicSelected(false);
                  }
                  onEventAddonItemToggle(waldofiedItem.id);
                }}
                title={waldofiedMeta.title}
                subtitle={waldofiedMeta.subtitle}
                description={waldofiedMeta.description}
                features={
                  basicSelected ? meta.baseFeatures : waldofiedMeta.features
                }
                howItWorks={waldofiedMeta.howItWorks}
                eventAddonItem={waldofiedItem}
                basicSelected={basicSelected}
                isWaldofiedAlbum
              />
            </div>
          </Fragment>
        )}
      </div>
      {albumAddonItems.length > 0 && (
        <div className="w-100">
          <p className="text-muted text-uppercase text-extra-bold mt-3 mb-0">
            Special Albums
          </p>

          {albumAddonItems.map(eventAddonItem => {
            const eventAddonItemMeta =
              meta.addonItemDetails[eventAddonItem.types[0]];

            const isSelected = selectedEventAddonItemIds.includes(
              eventAddonItem.id,
            );

            return (
              <EventAddonItem
                key={eventAddonItem.id}
                isSelected={isSelected}
                onToggleSelect={onEventAddonItemToggle}
                title={eventAddonItemMeta.title}
                subtitle={eventAddonItemMeta.subtitle}
                description={eventAddonItemMeta.description}
                features={eventAddonItemMeta.features}
                howItWorks={eventAddonItemMeta.howItWorks}
                eventAddonItem={eventAddonItem}
                className="mt-2"
              />
            );
          })}
        </div>
      )}
      {purchasedAddonItems.length > 0 && (
        <div className="w-100">
          <hr className="w-100" />
          <span className="text-muted text-uppercase text-extra-bold">
            Purchased
          </span>
          {purchasedAddonItems.map(purchasedAddonItem => {
            const eventAddonItem = {
              id: purchasedAddonItem.id,
              type: purchasedAddonItem.event_addon_type,
            };

            const eventAddonItemMeta =
              meta.addonItemDetails[eventAddonItem.type];

            if (!eventAddonItemMeta) {
              return null;
            }

            return (
              <div className="freemium-event-option mt-2">
                <EventAddonItem
                  key={eventAddonItem.id}
                  title={eventAddonItemMeta.title}
                  subtitle={eventAddonItemMeta.subtitle}
                  description={eventAddonItemMeta.description}
                  features={eventAddonItemMeta.features}
                  eventAddonItem={eventAddonItem}
                  className="mt-2"
                  isPurchased
                  disabled
                />
              </div>
            );
          })}
        </div>
      )}
      <div className={checkoutContainerClasses}>
        <Button
          onClick={onPurchase}
          disabled={
            // If we do not display the default, we only care for add-ons.
            (!displayDefaultPackage &&
              (selectedEventAddonItemIds ?? []).length === 0) ||
            // If we display the default, we can either, choose it or addons.
            (!includePhotosAlbum &&
              (selectedEventAddonItemIds ?? []).length === 0)
          }
          className={submitBtnClasses}
        >
          {forUpgrade ? 'Upgrade now' : 'Continue'}{' '}
          {checkoutPrice > 0 && <>(${formatPriceForDisplay(checkoutPrice)})</>}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const withUserData = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

FreemiumEventPurchase.propTypes = propTypes;

export default compose(
  connect(mapStateToProps),
  withUserData,
)(FreemiumEventPurchase);
